import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Services Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Services was one of
 * the eleven still carrying it: a two-paragraph series description under an amber
 * note saying the sector's own words had never been supplied. They were supplied on
 * 2026-08-28 — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını
 * tasarla"*, the same words the Technology, Tourism and Utilities cuts arrived in —
 * with a four-part full-page capture of digitopia.co's Services Industry Focus page
 * as the reference, plus four files: the report mockup, Figure 1, and two
 * overlapping crops of the cover. So this is the twenty-fourth designed report page
 * and the ninth focus cut to arrive with **both** of its figures.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from
 * the reference capture. The *shape* of the page is certain; the *wording* is a
 * careful reading of body copy that renders a few pixels tall. Every band that
 * carries prose therefore carries a `check`, exactly as `content/daimiTechnology.ts`
 * and `content/daimiTourism.ts` do, and `daimiServicesChecks()` feeds them into the
 * same manifest as the rest of the site's amber notes so a production build cannot
 * ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the
 * closing band's destination, and it was decided in the request: *"en aşağıda
 * önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That
 * band is `content/reportClosing.ts`, shared by every report page but three, and it
 * already points at the Digital & AI Maturity Report 2026 — so this page inherits
 * the answer rather than restating it, and the reference capture agrees: its Global
 * Perspective card names the 2026 report too.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These
 * documents are prerendered with no client bundle at all (see `src/prerender.tsx`),
 * so an embedded form script would never run, and the other reports on this site
 * hand the reader the PDF from our own bucket instead. So every Download button
 * here is that PDF, and `DAIMI_SV_DOWNLOAD.check` is what keeps the question of
 * gating open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built: it is what digitopia.co serves, and tidying it would break the inbound
 * links this page exists to keep working. Note that it is **not** the Public
 * Services cut, which is a separate report with a slug of its own
 * (`…-2026-public-services-industry`) and no designed page yet.
 */
export const DAIMI_SV_SLUG = 'digital-and-ai-maturity-report-2026-services-industry'

/**
 * The hero's two paragraphs, read off the reference capture.
 *
 * Two, not three — this cut does not close its hero with `FOCUS_CLOSE` the way the
 * Airlines and Banking pages do. The second paragraph is the sector's own
 * "to lead in 2026" line instead, and it is the page's argument in one sentence.
 *
 * The two figures in the first paragraph are set in bold on the live page — 2.71
 * for the sector against a 2.93 baseline. They are plain here, because this page
 * renders its paragraphs as strings rather than as markup, and the second of them
 * is the only place on the page where the benchmark average appears at all. The
 * sector's own 2.71 is also the 2025 average in Figure 1, so the two agree.
 */
export const DAIMI_SV_INTRO = {
  body: [
    'The services industry scores 2.71 in the 2026 DAIMI benchmark, trailing the 2.93 baseline due to fragmented operating models and inconsistent processes. As AI increasingly automates knowledge work, firms risk falling behind competitors who deliver faster, more consistent outcomes through industrialized models.',
    'To lead in 2026, organizations must modernize their core and transform individualized expertise into a scalable, enterprise-wide advantage.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in
   * the reports grid carries — and the file attached with this request is the same
   * artwork much larger, so it replaced it under the same key rather than landing
   * beside it under a second name. The index card reads the same path and simply
   * gets a sharper cover out of it. Same call the Airlines, Automotive, Banking,
   * Construction, Utilities, Tourism and Technology cuts made.
   *
   * **With the same caveat those covers had, file for file.** It arrived as two
   * captures, "(1 of 2)" and "(2 of 2)", and they are not two halves — they are
   * overlapping crops of the same square artwork, one cut off at the bottom through
   * the logo and one cut off at the top through the title's margin. The second is
   * the one held here, because it is the only one that contains the whole design; it
   * is 2000 × 1848 rather than square, so it is missing a sliver of the top margin,
   * and the index card's square crop trims about 4% off each side. `check` asks for
   * the file itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-services-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Services Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The two paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — including the two figures in the first one, 2.71 for the sector against a 2.93 baseline. The live hero has no cover beside it — the right of the hero is empty but for the ribbon — and the cover is in the hero here because that is what the other designed report pages do; say if the live emptiness was the point. And the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the sharper one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by four lines that each open with a
 * bolded label — "The Scaling Crisis:", "Data and Integration Gaps:", "Untapped
 * Potential:", "Cultural Barriers:". Split into `label` and `body` here so the
 * label can be set rather than emphasised inside a sentence, which is what a
 * `<strong>` in the middle of a paragraph gives.
 */
export const DAIMI_SV_FINDINGS = {
  heading: 'DAIMI Findings: Industrialization vs. Fragmentation',
  lede: 'The maturity distribution for services is wide, yet the center remains clearly below the benchmark average, indicating that both agility and resilience are currently underdeveloped.',
  points: [
    {
      label: 'The Scaling Crisis',
      body: 'Relying on variable delivery models creates pockets of excellence but hinders enterprise-wide replication.',
    },
    {
      label: 'Data and Integration Gaps',
      body: 'Disconnected tools for client and project data prevent AI from being embedded into standard delivery workflows.',
    },
    {
      label: 'Untapped Potential',
      body: 'The sector’s high-value, repeatable knowledge work is ready for AI augmentation to drive productivity and quality.',
    },
    {
      label: 'Cultural Barriers',
      body: 'Prioritizing individualized autonomy over standardized methods slows adoption and increases fragmentation.',
    },
  ],
  /**
   * Figure 1 itself — the six-year study-results chart supplied with this request,
   * the same shape the Automotive and Banking cuts carry rather than the vitality
   * scatter the Technology and Tourism cuts do or the portrait score range the
   * Utilities cut does. Six years, 2020 to 2025, each one a range from the laggard
   * through the average to the leader with that year's company count in the header.
   * It is 1200 × 627 on a white ground, so it gets a white card and fills it whole.
   */
  photo: {
    src: '/photo/daimi-services-study-results.webp',
    alt: 'DAIMI study results for the services industry, 2020 to 2025: the laggard, average and leader score each year, from 1.40–2.20–2.60 in 2020 to 1.98–2.71–3.48 in 2025',
  } satisfies Photo,
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Services Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole, including its own year-by-year company counts (2 in 2020 rising to 152 in 2025) — the live caption carries neither those nor a year, so they are stated in the alt text and nowhere else. Its 2025 average, 2.71, is the same number the hero opens with, which is the one cross-check the page can do on itself. What wants a reading against digitopia.co is the prose: the lede and the four findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_SV_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Services',
  body: [
    'To lead in 2026, services executives must break the “maturity ceiling” by shifting from tool-driven initiatives to outcome-driven transformation. Success requires simplifying core platforms to unlock visibility and investing in upskilling to ensure new delivery habits actually change how work is done.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn expertise into a compounding digital business model.',
  ],
  /** The mockup supplied with this request — the services cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-services-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Services Focus',
  } satisfies Photo,
  check:
    'Two things here. The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. And on the live page this band is text only: the right half of that row is empty, and the report mockup you attached does not appear on the page anywhere. It is beside the analysis here because that is where the other designed focus cuts put theirs and because the file would otherwise go unused. Say if the live band’s emptiness was deliberate.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every
 * report page but three — and the reference capture for this page shows exactly
 * that pair, pointing at the Digital & AI Maturity Report 2026, which is the "2024
 * değil 2026" the request asked for. Nothing to change: the shared band already
 * links to `DAIMI_SLUG`.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_SV_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Services Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiTechnologyChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut
 * arrived with both of its figures and its cover. Three of the notes are a
 * transcription nobody has read against the original, and one is a decision about
 * gating that belongs to marketing.
 */
export function daimiServicesChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_SV_INTRO.check },
    { section: 'findings', check: DAIMI_SV_FINDINGS.check },
    { section: 'analysis', check: DAIMI_SV_ANALYSIS.check },
    { section: 'download', check: DAIMI_SV_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_SV_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
